import {
  Box,
  Button,
  ChakraStyledOptions,
  Flex,
  HStack,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Skeleton,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure,
} from '@chakra-ui/react';
import _ from 'lodash';
import React from 'react';

import { date as commonDate } from '@finley-technologies/common';

import {
  InvoicePaymentInKindItemGQLType,
  InvoicePaymentInKindItemsWithMetadataGQLType,
  InvoiceReportGQLType,
} from 'types/dashboard-core-gqlTypes.generated';

import { MarkAsNotifiedButton } from './MarkAsNotifiedButton';
import { colors } from 'packages/common-frontend/colors';
import { Icon } from 'packages/common-frontend/components/icons';
import { Typography } from 'packages/common-frontend/components/Typography';
import {
  dollarNumberWithCommas,
  percentageToPercentageLabel,
} from 'packages/common-frontend/utils';
import { LoanDetailsSideDrawer } from 'packages/invoices/ui/create/LoanDetailsSideDrawer';
import { DownloadAllLivePaymentInKindNoticesButton } from 'packages/invoices/ui/paymentInKind/DownloadAllLivePaymentInKindNoticesButton';
import { PaymentInKindNoticeDownloadButton } from 'packages/invoices/ui/paymentInKind/InvoicePaymentInKindDownloadButton';
import { useGetResolvedLoanByIdQuery } from 'packages/loans/requests/loans.gql.generated';

const TABLE_CELL_STYLES = {
  backgroundColor: 'white',
  color: colors.gray[500],
  whiteSpace: 'normal',
};

const HeaderCell = ({
  styles,
  children,
}: {
  styles?: ChakraStyledOptions;
  children: React.ReactNode;
}) => {
  return (
    <Th fontWeight={500} fontSize="14px" color="black" {...styles}>
      {children}
    </Th>
  );
};

const InvoicePaymentInKindItemRow = ({
  invoicePaymentInKindItem,
  hideBorder,
  reportDownload,
}: {
  invoicePaymentInKindItem: InvoicePaymentInKindItemGQLType;
  hideBorder: boolean;
  reportDownload: InvoiceReportGQLType | undefined;
}) => {
  return (
    <Tr key={invoicePaymentInKindItem.id}>
      <Td sx={TABLE_CELL_STYLES} border={hideBorder ? 'none' : undefined}>
        {invoicePaymentInKindItem.status === 'notified' ? (
          <Icon icon="check" color={colors.green[400]} />
        ) : (
          <MarkAsNotifiedButton
            invoicePaymentInKindItemId={invoicePaymentInKindItem.id}
          />
        )}
      </Td>
      <Td
        sx={{ fontWeight: 500, color: 'black' }}
        border={hideBorder ? 'none' : undefined}
      >
        {invoicePaymentInKindItem.capitalProviderOrganization.name}
      </Td>
      <Td
        sx={{
          ...TABLE_CELL_STYLES,
          border: hideBorder ? 'none' : undefined,
          textAlign: 'right',
        }}
      >
        {invoicePaymentInKindItem.capitalProviderProRata
          ? percentageToPercentageLabel(
              invoicePaymentInKindItem.capitalProviderProRata,
              6,
              true,
            )
          : '-'}
      </Td>
      <Td
        textAlign="right"
        sx={{
          ...TABLE_CELL_STYLES,
          border: hideBorder ? 'none' : undefined,
        }}
      >
        {dollarNumberWithCommas(invoicePaymentInKindItem.totalAmount)}
      </Td>
      <Td
        sx={{
          ...TABLE_CELL_STYLES,
          border: hideBorder ? 'none' : undefined,
        }}
      >
        {invoicePaymentInKindItem.notifiedDate
          ? commonDate.fullDateStrToHumanDateString(
              invoicePaymentInKindItem.notifiedDate,
            )
          : '-'}
      </Td>
      <Td
        sx={{
          ...TABLE_CELL_STYLES,
          border: hideBorder ? 'none' : undefined,
          textAlign: 'center',
        }}
      >
        {reportDownload ? (
          <PaymentInKindNoticeDownloadButton report={reportDownload} />
        ) : (
          <Tooltip label="No Payment In Kind Notice Found">
            <Button variant="v2-ghost" isDisabled>
              <Icon icon="arrow-down-to-bracket" />
            </Button>
          </Tooltip>
        )}
      </Td>
    </Tr>
  );
};

const PIKLoanDetailsSideDrawer = ({
  loanId,
  isOpen,
  onClose,
}: {
  loanId: string;
  isOpen: boolean;
  onClose: () => void;
}) => {
  const {
    data: loanData,
    loading: isLoanLoading,
  } = useGetResolvedLoanByIdQuery({
    variables: { loanId },
  });

  if (isLoanLoading || !loanData) {
    return <Skeleton height="400px" />;
  }

  return (
    <LoanDetailsSideDrawer
      isOpen={isOpen}
      onClose={onClose}
      loan={loanData.loan}
    />
  );
};

export const InvoicePaymentInKindTable = ({
  invoicePaymentInKindItemsWithMetadata,
  invoiceReportsForCapitalizedLoan,
  invoiceCreditFacilityId,
  invoiceLoanId,
}: {
  invoicePaymentInKindItemsWithMetadata: InvoicePaymentInKindItemsWithMetadataGQLType;
  invoiceReportsForCapitalizedLoan: InvoiceReportGQLType[];
  invoiceCreditFacilityId: string;
  invoiceLoanId?: string;
}) => {
  const {
    isOpen: isLoanSideDrawerOpen,
    onOpen: onLoanSideDrawerOpen,
    onClose: onLoanSideDrawerClose,
  } = useDisclosure();

  const isSelfLoan =
    invoicePaymentInKindItemsWithMetadata.capitalizedLoanId === invoiceLoanId;

  const sortedPaymentInKindItems = _.sortBy(
    invoicePaymentInKindItemsWithMetadata.invoicePaymentInKindItems,
    (item) => item.capitalProviderOrganization.name,
  );

  return (
    <>
      <Flex w="100%">
        <Typography fontWeight="bold">PIK to&nbsp;</Typography>
        <Box cursor="pointer" onClick={onLoanSideDrawerOpen}>
          <Typography fontWeight="bold" colorScheme="blue" variant="base-muted">
            {isSelfLoan
              ? 'This Loan'
              : invoicePaymentInKindItemsWithMetadata.capitalizedLoanName}
          </Typography>
        </Box>
      </Flex>

      <PIKLoanDetailsSideDrawer
        loanId={invoicePaymentInKindItemsWithMetadata.capitalizedLoanId}
        isOpen={isLoanSideDrawerOpen}
        onClose={onLoanSideDrawerClose}
      />

      <Table>
        <Thead>
          <Tr>
            <HeaderCell styles={{ width: '15%' }}>Notified</HeaderCell>
            <HeaderCell styles={{ width: '20%' }}>Lender</HeaderCell>
            <HeaderCell
              styles={{
                width: '20%',
                textAlign: 'right',
              }}
            >
              Lender Share
            </HeaderCell>
            <HeaderCell styles={{ width: '20%', textAlign: 'right' }}>
              Interest Paid
            </HeaderCell>
            <HeaderCell styles={{ width: '20%' }}>Notified Date</HeaderCell>
            <Th
              fontWeight={500}
              fontSize="14px"
              color="black"
              width="5%"
              textAlign="center"
            >
              <Menu>
                <MenuButton
                  fontSize="inherit"
                  fontWeight="inherit"
                  aria-label="Download and email all funding requests"
                >
                  <HStack>
                    <Typography fontWeight="semibold" variant="detail">
                      Notice
                    </Typography>
                    <Icon icon="chevron-down" />
                  </HStack>
                </MenuButton>
                <MenuList>
                  <MenuItem as="div">
                    <DownloadAllLivePaymentInKindNoticesButton
                      invoiceReports={invoiceReportsForCapitalizedLoan}
                      creditFacilityId={invoiceCreditFacilityId}
                    />
                  </MenuItem>
                </MenuList>
              </Menu>
            </Th>
          </Tr>
        </Thead>
        <Tbody>
          {sortedPaymentInKindItems.map((invoicePaymentInKindItem, idx) => {
            // hide the bottom border on the last row
            const hideBorder =
              idx ===
              invoicePaymentInKindItemsWithMetadata.invoicePaymentInKindItems
                .length -
                1;
            const reportDownload = invoiceReportsForCapitalizedLoan.find(
              (download) =>
                download.capitalProviderId ===
                  invoicePaymentInKindItem.capitalProviderOrganization.id &&
                download.reportType === 'paymentInKindNotice',
            );

            return (
              <InvoicePaymentInKindItemRow
                key={invoicePaymentInKindItem.id}
                invoicePaymentInKindItem={invoicePaymentInKindItem}
                hideBorder={hideBorder}
                reportDownload={reportDownload}
              />
            );
          })}
        </Tbody>
      </Table>
    </>
  );
};
